/**
 * BatchGeoGoogleMapsManager is in charge of creating a Google maps instance.
 * Given a few options to initialize this map, you can get an instance of it
 * from BatchGeoGoogleMapsManager.getMap(). It will return a Google Map Object
 * so you can getBounds(), set markers on this map, etc.
 */
(function (window) {
  function BatchGeoGoogleMapsManager(options) {
    options = options || {};
    this.mapLoaded = false;
    this._initializeManager(options);
  }

  function getMapTypeIds(options) {
    if (options.markersData.style) {
      return [
        options.markersData.style.indexOf("simple") == -1
          ? "simpleMap"
          : google.maps.MapTypeId.ROADMAP,
        google.maps.MapTypeId.TERRAIN,
        google.maps.MapTypeId.SATELLITE,
        google.maps.MapTypeId.HYBRID,
      ];
    }
    return ["simpleMap"];
  }

  /**
   * Function to determine if scroll to zoom should be on or off. Will return true
   * if it should scrolling should zoom and false if not.
   *
   * NOTE: the code can be simplified into one line or a complex set of &&s
   * and ||s however, it's purposely broken out to be very clear what the
   * priorities and conditions are.
   *
   * @param {object} mapOptions The options you would pass to a new Google Map
   * @return {boolean} Returns true for zooming and false to not zoom
   */
  function enableScrollZoom(mapOptions) {
    // If its edit or iframe detect do NOT enable scroll to zoom
    if (BatchGeoStore.getState().App.isEdit || BatchGeo.iframeDetect()) {
      return false;
    }

    // If it's advanced mode enable scroll to zoom since the page will never
    // scroll anyway
    if (BatchGeoStore.getState().App.advanced) {
      return true;
    }

    // If there's 0 marker data enable scroll to zoom
    if (
      mapOptions.markersData.mapRS &&
      mapOptions.markersData.mapRS.length === 0
    ) {
      return true;
    }

    // If it's a labeled marker like numbers or letters and not just a plain
    // marker then do NOT enable scroll to zoom
    if (mapOptions.markersData.labeltype_sel !== "plain") {
      return false;
    }

    // For everything else, enable scroll to zoom
    return true;
  }

  BatchGeoGoogleMapsManager.prototype._initializeManager = function (options) {
    var self = this;

    this.settings = $.extend(
      {
        mapContainer: "mapDiv",
        mapTypeControlOptions: google.maps.ControlPosition.TOP_RIGHT,
        zoomControlOptions: google.maps.ControlPosition.LEFT_TOP,
        mapTypeControlStyle: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        streetViewControlOptions: google.maps.ControlPosition.LEFT_TOP,
        minZoom: 2,
        maxZoom: options.markersData.hideaddr_cb ? 12 : 18,
        zoom: 12,
        mapTypeIds: getMapTypeIds(options),
        scrollwheel: enableScrollZoom(options),
        gestureHandling:
          !BatchGeo.iframeDetect() && !BatchGeoStore.getState().App.isEdit
            ? "greedy"
            : "cooperative",
        disableDefaultUI: window.printMode,
        streetViewControl:
          options.markersData.hideaddr_cb || window.printMode ? false : true,
        fullscreenControl: false,
      },
      options,
    );

    $(window).on("batchgeo.advanced", function () {
      self.getMap().set("scrollwheel", enableScrollZoom(options));
    });

    return this;
  };

  function dispatchOpenCachedMarker(marker) {
    BatchGeoStore.dispatch({
      type: "MAPSMARKERS_MANAGER_MARKER_CLICKED",
      marker: marker,
    });
  }

  BatchGeoGoogleMapsManager.prototype.doMap = async function (reset = false) {
    if (!this.settings.markersData.mapRS) {
      return;
    }

    let center;
    let zoom;
    if (reset && this.map instanceof google.maps.Map) {
      center = this.map.getCenter();
      zoom = this.map.getZoom();
    }

    var self = this;
    BatchGeo.timer("start doMap()");

    $("#" + this.settings.mapContainer).show();
    // initial loop, prep some stuff
    BatchGeo.timer("begin cleanup marker data");
    for (var i = 0; i < this.settings.markersData.mapRS.length; i++) {
      var rowMarker = this.settings.markersData.mapRS[i];
      if (typeof window.cacheRow == "function") window.cacheRow(rowMarker); // only cacheRows on edit page
      rowMarker.lt = parseFloat(rowMarker.lt);
      rowMarker.ln = parseFloat(rowMarker.ln);

      if (
        this.settings.markersData.dist_cb_status &&
        this.settings.markersData.mapRS[0].lt &&
        Math.abs(rowMarker.lt) > 0
      ) {
        // calculate distances
        if (i !== 0) {
          rowMarker.distance = BatchGeo.distance(
            parseFloat(this.settings.markersData.mapRS[0].lt),
            parseFloat(this.settings.markersData.mapRS[0].ln),
            rowMarker.lt,
            rowMarker.ln,
          ).toFixed(2);
        } else {
          rowMarker.distance = "0"; // keep this a string
        }
      }
    }

    let doBuild = true;
    const view =
      BatchGeo.iframeDetect() || BatchGeoStore.getState().App.isEdit
        ? null
        : BatchGeo.getMapViewCache(); // iframe || edit screen

    if (this.settings.markersData.dist_cb_status) {
      window.resolveDistanceFromFirst();
    }

    if (view && Object.size(view) > 0) {
      // cached view available
      this.settings.markersData.map_type = view.type;

      let colIndex;

      if (
        view.column &&
        view.column !== "Distance Between Points" &&
        this.settings.markersData.group_col >= 0
      ) {
        // we're looking here, but it's lost!
        colIndex = this.settings.markersData.columnNames.indexOf(view.column);

        if (colIndex < 0 && colIndex != window.per.group_col) {
          // Remove special characters from the column names and try to find a match.
          // The web image capture request flow can strip out special characters.
          const rgx = new RegExp("[^a-zA-Z0-9\\s\\-_]");
          let sanitizedColumnNames = this.settings.markersData.columnNames.map(
            (colName) => colName.replace(rgx, ""),
          );
          colIndex = sanitizedColumnNames.indexOf(view.column);
          if (!sanitizedColumnNames[colIndex]?.trim()) {
            // prevent match on empty strings
            // sanitization could theoretically empty a string
            colIndex = -1;
          }
        }

        if (colIndex != window.per.group_col) {
          window.batchGeoGoogleMapsMarker.MarkerLegend.selectColumn(
            colIndex,
            true,
          );
          doBuild = false;
        }
      }

      if (view.legendFilters && view.legendFilters.length) {
        if (doBuild)
          window.batchGeoGoogleMapsMarker.MarkerLegend.selectColumn(
            this.settings.markersData.columnNames.indexOf(
              view.legendFilters[0].column,
            ),
            true,
          );
        doBuild = false;
        window.batchGeoGoogleMapsMarker.MarkerLegend.importFilters(
          view.legendFilters,
        );
      }
    }

    //sets up legend and marker colors if it hasn't already been done from cache
    if (doBuild) window.batchGeoGoogleMapsMarker.MarkerGroups.buildGroups();

    BatchGeo.timer("begin map init");

    this.settings.markersData.style =
      this.settings.markersData.style || "simple";

    // only init if first request or on reset
    var map = this.getMap(reset);
    var customExtent = window.per.custom_extent;

    if (!map) {
      BatchGeo.customAlert(
        "Hmmm, appears the map is not loaded. Try refreshing your browser.",
      );
      return;
    }

    if (this.settings.markersData.style != "simple") {
      map.mapTypes.set(
        "simpleMap",
        new google.maps.StyledMapType(
          window.styles[this.settings.markersData.style],
          { name: "Map" },
        ),
      );
      map.setMapTypeId("simpleMap");
    } else {
      map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    }

    if (BatchGeoStore.getState().App.isMap) {
      google.maps.event.addListenerOnce(map, "idle", function () {
        // finish layout settings / snazzy fadey header / legend
        if ($("#" + self.settings.mapContainer).css("overflow") != "visible") {
          $("#" + self.settings.mapContainer)
            .css("overflow", "visible")
            .children()
            .css({
              overflow: "visible",
              "z-index": 0,
            })
            .children()
            .css({
              overflow: "visible",
              "z-index": 0,
            })
            .children()
            .css({
              overflow: "visible",
            });

          $("#topBar").css("z-index", 99);
          $("#topBG")
            .css("z-index", 99)
            .css("height", $("#topBar").outerHeight());
        }
        $(".gm-style").removeClass("gm-style"); // visual refresh workaround hack, lets us use our own css styling
      });
    } else {
      $("#mapheader").show();
      google.maps.event.addListenerOnce(map, "idle", function () {
        $(".gm-style").removeClass("gm-style");
      });
    }

    BatchGeo.timer("end map init");

    google.maps.event.trigger(map, "resize");

    this.setBounds(new google.maps.LatLngBounds());
    var bounds = this.getBounds();

    this.settings.markersData.view_sel = this.settings.markersData.view_sel
      ? this.settings.markersData.view_sel
      : 0;

    if (this.settings.markersData.map_type == "simpleMap")
      this.settings.markersData.map_type = google.maps.MapTypeId.ROADMAP;

    if (
      this.settings.markersData.map_type &&
      this.settings.markersData.map_type != "roadmap"
    ) {
      // will only be set if coming from view
      map.setMapTypeId(this.settings.markersData.map_type);
    } else {
      switch (this.settings.markersData.view_sel) {
        case 0:
          map.setMapTypeId(
            this.settings.markersData.style.indexOf("simple") == -1 &&
              this.settings.markersData.style != "simple"
              ? "simpleMap"
              : google.maps.MapTypeId.ROADMAP,
          );
          break;
        case 1:
          map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
          break;
        case 2:
          map.setMapTypeId(google.maps.MapTypeId.HYBRID);
          break;
        case 3:
          map.setMapTypeId(google.maps.MapTypeId.TERRAIN);
          break;
      }
    }

    this.mapLoaded = false;

    window.batchGeoGoogleMapsMarker.MarkerLegend.update(false);
    window.batchGeoMap.bgSize(true);

    var markersObjArray;

    if (reset) {
      window.batchGeoMap.mapReset();
      window.batchGeoGoogleMapsMarker.MarkerDrawer.draw();

      if (window.batchGeoMapContextMenu) {
        window.batchGeoMapContextMenu.destroy();
        window.batchGeoMapContextMenu = window.BatchGeoMapContextMenu();
        window.batchGeoMapContextMenu.init();
      }

      if (zoom && center) {
        google.maps.event.addListenerOnce(this.map, "idle", () => {
          this.map.setCenter(center);
          this.map.setZoom(zoom);
        });
      }
    }

    if (window.openLocator) {
      // locator running, prep markers but dont set zoom yet
      window.batchGeoGoogleMapsMarker.MarkerDrawer.draw();
      if (!window.geocodingEnabled) {
        window.batchGeoGoogleMapsMarker.zoomToMarkers();
      }
    } else if (!view || !view.zoom) {
      // draw default map
      window.batchGeoGoogleMapsMarker.MarkerDrawer.draw();
      markersObjArray = window.batchGeoGoogleMapsMarker.getMarkersObjArray();
      if (window.openRow >= 0 && markersObjArray[window.openRow] != null) {
        bounds.extend(markersObjArray[window.openRow].marker.position);
        dispatchOpenCachedMarker(markersObjArray[window.openRow].marker);
        map.fitBounds(bounds);
      } else {
        customExtent
          ? map.fitBounds(customExtent)
          : window.batchGeoGoogleMapsMarker.zoomToMarkers();
        if (markersObjArray.length == 1 && markersObjArray[0] !== null)
          dispatchOpenCachedMarker(markersObjArray[0].marker);
      }
    } else {
      // view available, use it
      if (view.zoom) {
        map.setCenter(new google.maps.LatLng(view.lat, view.lng));
        map.setZoom(view.zoom);
      }

      if (customExtent) {
        map.fitBounds(customExtent);
      }

      window.batchGeoGoogleMapsMarker.MarkerDrawer.draw();
      markersObjArray = window.batchGeoGoogleMapsMarker.getMarkersObjArray();

      // If there's no openRow globally (which takes precedence) but there's an
      // infowindow that was open on the last page load and its cached and that
      // marker is visible (in the markers array) display it.
      // NOTE: we do NOT do this if on mobile because it hides all the tooling
      // and tables
      if (
        window.openRow < 0 &&
        view.openRow >= 0 &&
        markersObjArray[view.openRow] &&
        !BatchGeoDevices.is("mobile")
      ) {
        dispatchOpenCachedMarker(markersObjArray[view.openRow].marker);

        // If there was a global openRow and its visible (not filtered out)
      } else if (
        window.openRow >= 0 &&
        markersObjArray[window.openRow] != null
      ) {
        dispatchOpenCachedMarker(markersObjArray[window.openRow].marker);

        // Finally, if neither of those check if there's only 1 marker visible
      } else if (markersObjArray.length == 1) {
        dispatchOpenCachedMarker(markersObjArray[0].marker);
      }
    }

    //init lazyload, and lazy load images with class `lazy`
    //in order to lazy load an image, you need to replace the src attribute with data-src
    new window.LazyLoad({
      elements_selector: ".lazy",
    });

    this.settings.markersData.max_lat = bounds.getNorthEast().lat();
    this.settings.markersData.max_lon = bounds.getNorthEast().lng();
    this.settings.markersData.min_lat = bounds.getSouthWest().lat();
    this.settings.markersData.min_lon = bounds.getSouthWest().lng();

    $("#buttonsDiv").show();
    $("#bottomAd").show();

    if (BatchGeo.getQueryVariable("q").length) {
      BatchGeo.timer("runSearch() from passed URL var");
      window.batchGeoOmnibox.runSearch(BatchGeo.getQueryVariable("q"));
    }

    // Fire an event to know when the map is loaded
    BatchGeoStore.dispatch({ type: "MAP_LOADED" });

    google.maps.event.addListenerOnce(map, "tilesloaded", function () {
      //this part runs when the mapobject is created and rendered
      if (self.mapLoaded) return;
      self.mapLoaded = true;
      BatchGeo.timer("tiles loaded");
      window.batchGeoGoogleMapsMarker.MarkerDrawer.checkLabels();
      window.BatchGeoTrack.event({
        featureName: "map viewed",
        eventName: "GA4 - Pageview",
        eventValue: "map was viewed",
      });
      google.maps.event.addListener(map, "zoom_changed", function () {
        window.BatchGeoTrack.event({
          featureName: "GA4 - Zoom",
          eventName: "GA4 - Zoom",
          eventValue: map.getZoom(),
        });
      });
      google.maps.event.addListener(map, "dragend", function () {
        window.BatchGeoTrack.event({
          featureName: "GA4 - Pan",
          eventName: "GA4 - Pan",
          eventValue: map.getCenter().lat() + ", " + map.getCenter().lng(),
        });
      });
    });

    BatchGeo.timer("end doMap()");

    return map;
  };

  function _handleSelectedMarkerCountPosition() {
    const windowWidth = window.innerWidth;
    const currentSelectedMarkerCount =
      BatchGeoStore.getState().RowSelect.selection.length;
    const selectedMarkerCountEl = $("#mapDiv .selected-marker-count");
    const hasSpecialClass = selectedMarkerCountEl.hasClass("smc-up");
    if (
      windowWidth <= 800 &&
      windowWidth >= 600 &&
      currentSelectedMarkerCount > 1
    ) {
      if (!hasSpecialClass) {
        selectedMarkerCountEl.addClass("smc-up");
      }
    } else {
      if (hasSpecialClass) {
        selectedMarkerCountEl.removeClass("smc-up");
      }
    }
  }

  /**
   * Returns the zoom level at which no tiles will be repeated. All tiles are
   * based off of a 256px tile size.
   *
   * {@link https://developers.google.com/maps/documentation/javascript/coordinates}
   */
  function _getMinZoomLevel(map) {
    if (map && map.getDiv()) {
      return Math.ceil(Math.log(map.getDiv().offsetWidth / 256) / Math.log(2));
    }
  }

  /**
   * Forces the max zoom out level to never show the same location on the map
   * twice. If you have a very wide monitor you won't be able to zoom out as far
   * as someone with a small screen.
   *
   * @param {Object} map A Google Maps instance
   * @return {undefined}
   */
  function _initMaxZoomOutListener(map) {
    // Listen for window resizes so we can zoom in if needed
    window.addEventListener("resize", () => {
      _handleSelectedMarkerCountPosition();
      const minZoom = _getMinZoomLevel(map);
      map.setOptions({
        minZoom,
      });
    });

    // doesn't throw errors or warnings
    // 'map' is not a domNode, throws addEventListener is not a function when updated
    google.maps.event.addListenerOnce(map, "bounds_changed", function () {
      const minZoom = _getMinZoomLevel(map);
      map.setOptions({
        minZoom,
      });
    });
  }

  BatchGeoGoogleMapsManager.prototype.getMap = function (reset = false) {
    if (reset) {
      this.map = null;
      BatchGeoStore.dispatch([
        {
          type: "MARKER_DRAWER_ENABLE_DRAWING",
        },
      ]);
    }

    if (this.map instanceof google.maps.Map) {
      window.paneFinder = new window.PaneFinder(this.map);
      return this.map;
    }

    if (document.getElementById(this.settings.mapContainer) == null) {
      console.warn(
        "invalid mapContainer ID. you must pass a valid DOM element ID where you want the map to appear.",
      );
      return {};
    }

    let mapOptions = {
      disableDefaultUI: true,
      // Prevents gray bars when panning too far North or South
      restriction: {
        latLngBounds: {
          north: 85,
          south: -85,
          west: -180,
          east: 180,
        },
      },
      mapTypeControl: true,
      mapTypeControlOptions: {
        mapTypeIds: this.settings.mapTypeIds,
        style: this.settings.mapTypeControlStyle,
        position: this.settings.mapTypeControlOptions,
      },
      zoomControlOptions: {
        position: this.settings.zoomControlOptions,
      },
      streetViewControlOptions: {
        position: this.settings.streetViewControlOptions,
      },
      scaleControl: window.per.isMapPreview ? false : true,
      panControl: false,
      keyboardShortcuts: false,
      maxZoom: this.settings.maxZoom,
      minZoom: this.settings.minZoom,
      zoom: this.settings.zoom,
      scrollwheel: this.settings.scrollwheel,
      zoomControl: true,
      gestureHandling: this.settings.gestureHandling,
      streetViewControl: this.settings.streetViewControl,
      fullscreenControl: this.settings.fullscreenControl,
    };

    const heatMapState = BatchGeoStore.getState().HeatMap;
    const heatMapStorage = localStorage.getItem("HeatMap");
    let heatMapEnabled = false;
    if (heatMapState.initialized) {
      heatMapEnabled = heatMapState.enabled;
    } else if (heatMapStorage) {
      const mapHeatMapStatus =
        JSON.parse(heatMapStorage)[BatchGeoStore.getState().App.mapId];
      if (mapHeatMapStatus) {
        heatMapEnabled = mapHeatMapStatus;
      }
    }
    if (heatMapEnabled) {
      window.per.map_render_type = { type: "marker" };
    }

    if (
      window.per.map_render_type?.type === "choropleth" &&
      !window.per.disableChoropleth
    ) {
      mapOptions.mapId = "a71816e22e08cc0f";
      mapOptions.isFractionalZoomEnabled = false;
    }

    const map = new google.maps.Map(
      document.getElementById(this.settings.mapContainer),
      mapOptions,
    );

    this.map = map;
    window.paneFinder = new window.PaneFinder(this.map);

    BatchGeoStore.dispatch({
      type: "SET_LOADED",
      loaded: true,
    });

    google.maps.event.addListenerOnce(map, "idle", function () {
      BatchGeoStore.dispatch({
        type: "SET_RENDERED",
        rendered: true,
      });
    });

    _initMaxZoomOutListener(map);
    return map;
  };

  BatchGeoGoogleMapsManager.prototype.setBounds = function (bounds) {
    this.bounds = bounds;
  };

  BatchGeoGoogleMapsManager.prototype.getBounds = function () {
    return this.bounds;
  };

  /**
   * This function might break in the future if Google Maps changes any of their
   * DOM structure. In the meantime it assumes this structure and returns a
   * string of the type.
   *
   * @return {string} The distance unit. Will be "mi", "ft", "km", or "m"
   */
  BatchGeoGoogleMapsManager.prototype.getDistanceScale = function () {
    var $mapDiv = $(this.getMap().getDiv());
    var $unitElement = $($mapDiv.find("div button.gm-style-cc div span")[0]);
    var unitName = $unitElement.text().trim().split(" ").pop();

    // If the selector ever does fail we handle it by checking if unitName is
    // not what we expect then send an error to the console and we default to km.
    if (["mi", "ft", "km", "m"].indexOf(unitName) == -1) {
      console.info(
        "Could not find measurement. Defaulting to `km` unit scale.",
      );
      return "km";
    }

    return unitName;
  };

  /**
   * Given two markers (marker1 and marker2) it finds the distance between the two
   * in the distance specified by Google Maps which we look up in getDistanceScale.
   *
   * @param {object} marker1 A google marker instance
   * @param {object} marker2 A google marker instance
   * @return {string} Returns a string rounded to two decimal places (ex: 123.45)
   */
  BatchGeoGoogleMapsManager.prototype.getDistanceBetweenMarkers = function (
    marker1,
    marker2,
  ) {
    var distanceUnit = BatchGeo.getEquatorialRadius(this.getDistanceScale());

    var lat1 = marker1.lat();
    var lng1 = marker1.lng();
    var lat2 = marker2.lat();
    var lng2 = marker2.lng();

    // Get the distance
    var dist =
      distanceUnit *
      Math.acos(
        Math.cos(BatchGeo.radians(90 - lat1)) *
          Math.cos(BatchGeo.radians(90 - lat2)) +
          Math.sin(BatchGeo.radians(90 - lat1)) *
            Math.sin(BatchGeo.radians(90 - lat2)) *
            Math.cos(BatchGeo.radians(lng1 - lng2)),
      );

    // Force the distance into a positive then round it to 2 decimal points. This
    // also converts it to a string
    return Math.abs(dist).toFixed(2);
  };

  window.BatchGeoGoogleMapsManager = BatchGeoGoogleMapsManager;
})(window);
